import React from "react";

import "./css/ExperiencePage.css";

const jobs = [
  {
    company: "TEKsystems / Symbotic",
    dates: "Mar 2026 – Aug 2026",
    title: "Senior Software Engineer",
    points: [
      "Implement and maintain production Python/TypeScript services; own bug fixes, features, and refactors through tests and review.",
      "Design MCP tools that expose real systems to AI agents; debug tool-use and runtime failures; improve contracts and performance.",
      "Collaborate remotely with distributed engineering teams on design tradeoffs, code review, and operational reliability.",
      "Containerized and shipped services on GKE / Cloud Run; investigated performance and reliability issues in distributed delivery.",
    ],
  },
  {
    company: "US Tech Solutions",
    dates: "Apr 2025 – Feb 2026",
    title: "Software Engineer",
    points: [
      "Delivered Python backend features and MCP/LangGraph agent workflows; investigated defects and shipped maintainable fixes.",
      "Added tests, logging, and human-in-the-loop checks so agent behavior can be verified deterministically.",
      "Deployed containerized services on AWS; refactored evaluation prototypes into reusable components.",
      "Used AI coding agents for implementation and debugging, then validated generated changes with tests and technical review.",
    ],
  },
  {
    company: "Highlight Technologies",
    dates: "Mar 2024 – Mar 2025",
    title: "Software Engineer / Full-Stack Developer",
    points: [
      "Implemented TypeScript/Python product features; refactored and optimized event-driven AWS services (DynamoDB, SNS, SQS).",
      "Debugged application, data, and deployment issues; Terraform/CI/CD; production support.",
      "Collaborated with product and business stakeholders to translate requirements into technical solutions.",
    ],
  },
  {
    company: "DataAnnotation.tech",
    dates: "Jan 2024 – Feb 2024",
    title: "Software Engineer",
    points: [
      "Created and evaluated coding solutions for AI training/evaluation: correctness, style, maintainability, and performance.",
      "Reviewed multi-language implementations (Python, JavaScript, C++, C#, SQL) against expected behavior.",
      "Identified bugs, weak abstractions, and faster alternatives using a reference-solution / rubric mindset.",
    ],
  },
  {
    company: "Apex Systems / Bank of America",
    dates: "Dec 2021 – Dec 2023",
    title: "Application Architect",
    points: [
      "Led design and refactoring of large-scale distributed financial platforms (Java, Python, C#, SQL, AWS/Azure).",
      "Code review, root cause analysis, performance/scalability, and production support; promoted only changes that met maintainability and security bars.",
      "Designed and implemented cybersecurity solutions; configured HashiCorp Vault, CyberArk, and gMSA with best-practice documentation.",
      "Developed Java, C#, and Python programs to authenticate and rotate passwords; configured Active Directory, Oracle, and SQL Server service accounts to store secrets in HashiCorp Vault via REST APIs.",
    ],
  },
  {
    company: "PacteraEdge — UPS / Markaaz",
    location: "Portland, OR",
    dates: "Jan 2020 – Nov 2021",
    title: "Full-Stack Developer",
    points: [
      "Implemented a Rust program to process CSV data files for operational workflows—parsing records, transforming fields, and producing deterministic outputs for downstream pipelines.",
      "Used Rust for performance-minded file processing alongside Python/Spark pipelines and Java/Python automation on the same project.",
      "Debugged malformed input, encoding, and edge-case rows; kept the tool maintainable as a reference implementation for batch CSV handling.",
      "Developed an Elliptic Curve Digital Signature Algorithm for a Node.js application to secure transactions on Markaaz (markaaz.com).",
      "Developed front-end (AngularJS) and back-end (Node.js) features, plus REST API microservices for a B2C cloud application.",
      "Built a blockchain digital-identity application using an Ethereum ledger.",
      "Developed Java applications processing large metadata repositories, plus Python and PowerShell scripts to load metadata into existing and new repositories.",
      "Wrote stored procedures to insert, delete, and update data in SQL Server, and Java programs to use those procedures.",
      "Set up an FTP server over SSL to transfer files between servers.",
      "Developed a Java program that generates metadata and models for entities, attributes, application types, type lists, list items, and their relationships.",
      "Used Go for build and deployment of cloud-native applications on Azure.",
      "Developed and deployed data and machine learning pipelines using Kedro, Data Validator Tool, Kedro Streaming, Spark, and TensorFlow.",
      "Loaded data into Data Lake, Blob, S3, and BigQuery; converted Databricks pipelines to Synapse pipelines.",
      "Parsed SSIS packages to extract SQL and process tasks, then built a CSV report of task details and execution flow.",
    ],
  },
  {
    company: "Infosys — Adidas",
    location: "Portland, OR",
    dates: "Apr 2018 – Dec 2019",
    title: "Technology Lead",
    points: [
      "Production defect resolution and platform enhancements with globally distributed (remote) teams.",
      "Specs, test plans, go-live docs; Docker/Kubernetes; mentored engineers.",
      "Independently developed and reviewed code and contributed to the go-live plan.",
      "Fixed defects in production for e-commerce web and mobile applications.",
      "Deployed, configured, developed, and supported Salesforce.com features.",
      "Wrote Python scripts to analyze big data and generate reports.",
      "Configured Akamai CDN domains, permission rules, and cache clearing for web applications.",
    ],
  },
  {
    company: "Intel",
    location: "Portland, OR",
    dates: "Apr 2016 – Mar 2018",
    title: "Python Developer / Validation / Firmware",
    roles: [
      {
        title: "Python Developer",
        dates: "Jun 2017 – Mar 2018",
        points: [
          "C++ and Python tooling, REST APIs, automated tests, performance-oriented validation, Linux.",
          "Applied Behavior Driven Development; implemented BIOS automation test cases using Python and Gherkin.",
          "Wrote REST APIs and object-oriented Python tools for failure analysis and test-result reporting.",
          "Developed Windows 10 and IoT automation and ran tests on the TWS testing engine.",
        ],
      },
      {
        title: "System Validation Engineer",
        dates: "Jan 2017 – Jun 2017",
        points: [
          "Implemented a WinIoT automation framework in PowerShell and Python for Joule 570x.",
          "Installed and troubleshot drivers, firmware, and operating systems on Joule 570x devices.",
          "Wrote C/C++ programs to test memory, CPU, and disk; measured WinIoT platform performance.",
          "Built a lab environment with Windows Server 2016, Hyper-V, Windows 10, and Ubuntu.",
        ],
      },
      {
        title: "Firmware Software Engineer",
        dates: "Apr 2016 – Dec 2016",
        points: [
          "Installed drivers and firmware on Intel test targets (processors, PCH, Beagle I2C, Aardvark, relays, Saleae Logic Analyzer).",
          "Ran and troubleshot C# and Python test scripts in a cloud application.",
          "Installed and used Wind River Simics; configured IPMI, IPC, and diagnostic bus interfaces.",
        ],
      },
    ],
  },
  {
    company: "Nike",
    location: "Portland, OR",
    dates: "Feb 2015 – Mar 2016",
    title: "Software Engineer",
    points: [
      "Java test automation; JMeter API performance testing during AWS migration; Jenkins CI.",
      "Wrote UI automation and performance scripts covering nike.net, including resource-monitoring tooling.",
      "Led UI automation and performance testing for nike.net (about $13 billion in orders in 2015).",
      "Released daily reports for UI automation and performance suites; two-week sprint cadence.",
    ],
  },
  {
    company: "McKesson Health Solutions",
    location: "Portland, OR",
    dates: "Jan 2012 – Feb 2015",
    title: "Software Engineer",
    points: [
      "Java/Spring production applications; APIs; SQL; full SDLC in regulated healthcare.",
      "Wrote test strategy, SOP, requirements-review, and report documents; designed regression automation for Contract Manager.",
      "Wrote Java Selenium/FluentLenium, QTP/VBScript, soapUI, and C# web-service test scripts.",
      "Configured client-server Java applications and SAML 2.0 single sign-on (OpenAM, OpenIdP, WSO2).",
      "Implemented a Spring Boot web application in Java with Hibernate and ES6 JavaScript, HTML, and CSS.",
    ],
  },
  {
    company: "Fiserv",
    location: "Corvallis, OR",
    dates: "Jan 2010 – Sep 2012",
    title: "Software Engineer",
    points: [
      "Java financial-platform software; Oracle/SQL Server; production delivery.",
      "Deployed builds on Tomcat and GlassFish on SUSE and RHEL.",
      "Designed, wrote, and executed XML test cases for TAF (Test Automation Framework) against the Acumen Java API.",
      "Wrote C# web-service test scenarios for batch processing; tested encryption algorithms.",
      "Installed SUSE, RHEL, Oracle 11g, and Microsoft SQL Server on physical and virtual machines.",
    ],
  },
];

export default function ExperiencePage() {
  return (
    <div className="wrapper-experience">
      <h1 className="title">Professional Experience</h1>
      <div className="content">
        {jobs.map((job) => (
          <article key={`${job.company}-${job.dates}`} className="job-card">
            <div className="job-header">
              <div>
                <h2 className="job-company">{job.company}</h2>
                <p className="job-title">{job.title}</p>
              </div>
              <p className="job-dates">
                {job.dates}
                {job.location ? ` · ${job.location}` : ""}
              </p>
            </div>
            {job.points && (
              <ul className="job-points">
                {job.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            )}
            {job.roles &&
              job.roles.map((role) => (
                <section key={role.title} className="job-role">
                  <h3 className="role-title">
                    {role.title}
                    <span className="role-dates">{role.dates}</span>
                  </h3>
                  <ul className="job-points">
                    {role.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </section>
              ))}
          </article>
        ))}
      </div>
    </div>
  );
}
